import aboutImage from "../assets/about-zs.svg";
import TechHero from "../components/TechHero";
import { use3DAnimation } from "../hooks/use3DAnimation";

export default function About() {
  const imageAnimation = use3DAnimation({ maxRotation: 12, scale: 1.08 });
  return (
    <div className="space-y-12">
      <TechHero
        kicker="Human-centered engineering"
        title="Products that feel premium, fast, and intuitive"
        subtitle="From architecture to UI, every layer is engineered for speed, trust, and long-term scale."
        ctaText="Meet the team"
      />
      <section className="perspective-container animate-3d-slide-up">
        <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="animate-3d-flip-in" style={{ animationDelay: '0.2s' }}>
            <p className="home-tagline">
              About <span className="brand-highlight">ZS NEXATECH</span>
            </p>
            <div className="page-title">
              <h1 className="mt-3 text-4xl font-bold text-white">
                Building digital products that drive real business growth.
              </h1>
            </div>
            <p className="mt-4 max-w-2xl text-gray-100">
              <strong>Fresh perspective.</strong> <strong>Modern technology.</strong> <strong>Dedicated focus.</strong> We specialize in HRMS, CRM, banking solutions, travel & transportation platforms, and industry-specific systems that simplify operations and unlock growth.
            </p>
          </div>
          <div className="flex justify-center lg:justify-end perspective-container">
              <img
                {...imageAnimation}
                src={aboutImage}
                alt="About ZS NEXATECH"
                className="w-full max-w-md rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 shadow-lg hover:shadow-2xl transition-all animate-3d-float"
                loading="lazy"
              />
            </div>
          </div>
      </section>

      <section className="card-grid grid gap-6 lg:grid-cols-3 perspective-container">
        {[
          {
            title: "🎯 Mission",
            desc: "Deliver exceptional software with transparent processes and measurable outcomes—built for long-term partnerships.",
          },
          {
            title: "🔭 Vision",
            desc: "Become a trusted technology partner for modernizing operations across education, healthcare, banking, and automotive sectors.",
          },
          {
            title: "💎 Values",
            desc: "Quality over shortcuts. Customer outcomes over vanity metrics. Clarity, integrity, and strong engineering culture.",
          },
        ].map((item) => (
          <div
            key={item.title}
            className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover-3d-lift transition-all duration-500 group relative overflow-hidden"
          >
            <div className="absolute inset-0 from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <h3 className="text-xl font-semibold text-white transform group-hover:scale-105 transition-transform duration-300">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="premium-panel glass-panel animated-border">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <span className="section-kicker holo-gradient text-slate-900">Operating principles</span>
            <h2 className="mt-4 text-3xl font-bold text-white">How we run every engagement</h2>
            <p className="mt-3 text-gray-100">
              Clear scope, consistent communication, and strong engineering standards—so your team never feels in the dark.
            </p>
            <div className="mt-5 timeline">
              {[
                ["Clarity first", "We define scope, milestones, and success metrics upfront."],
                ["Weekly visibility", "Demos, progress updates, and transparent risk tracking."],
                ["Quality gates", "Design QA, code reviews, and performance checks."],
              ].map(([title, desc]) => (
                <div key={title} className="timeline-step">
                  <span className="timeline-dot" aria-hidden="true" />
                  <h4 className="text-white font-semibold">{title}</h4>
                  <p className="text-sm text-gray-100">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-4">
            {[
              {
                title: "Strategy & UX",
                desc: "Workshops, discovery, and UX mapping to reduce rework.",
              },
              {
                title: "Engineering",
                desc: "Clean architecture, scalable APIs, and reliable releases.",
              },
              {
                title: "Launch readiness",
                desc: "Performance, monitoring, and post‑launch support.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5"
              >
                <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">⭐ Why teams choose ZS NEXATECH</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {[
            "Flexible engagement models with clear milestones",
            "Weekly progress demos with full transparency",
            "Modern, scalable tech stack (React, Spring Boot, PostgreSQL)",
            "UX that reduces friction and speeds adoption",
            "Clean, documented code with automated testing",
            "Fast response SLAs with post-launch support",
          ].map((item) => (
            <div
              key={item}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-md px-4 py-4 text-sm text-white/90 hover:bg-white/20 hover:border-white/40 hover:scale-105 transition-all duration-300"
            >
              ✔️ {item}
            </div>
          ))}
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">👨‍💻 Meet the Founder</h2>
            <p className="mt-2 text-sm text-gray-100 max-w-2xl">
              Founder-led delivery with hands-on full-stack leadership and clear, outcome-driven execution.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="/founder" className="btn-gold">View full profile</a>
            <a href="/contact" className="link-gold">Connect directly</a>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
          <h2 className="text-2xl font-bold text-white">🎯 Industry specialization</h2>
          <p className="mt-2 text-sm text-gray-100">
            Domain expertise that accelerates delivery and reduces rework:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-gray-100">
            <li>• <strong>Education:</strong> Student portals, attendance systems, fee management</li>
            <li>• <strong>Healthcare:</strong> Appointment booking, patient records, telemedicine</li>
            <li>• <strong>Automotive:</strong> Inventory management, service scheduling, dealer networks</li>
            <li>• <strong>Banking:</strong> Customer portals, KYC workflows, compliance reporting</li>
            <li>• <strong>Enterprise:</strong> HRMS with biometric integration, CRM with WhatsApp automation</li>
          </ul>
        </div>

        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
          <h2 className="text-2xl font-bold text-white">💪 Our guarantee to you</h2>
          <p className="mt-2 text-sm text-gray-100">
            Every project backed by clear quality commitments:
          </p>
          <div className="mt-4 space-y-3 text-sm text-gray-100">
            <p>✔ <strong>Fixed scope, timeline & budget</strong> with transparent milestones</p>
            <p>✔ <strong>Rigorous testing</strong> with QA and performance checks</p>
            <p>✔ <strong>90-day support</strong> with bug fixes and guided handover</p>
          </div>
        </div>
      </section>

      {/* Free Resources Section */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">📚 Free Resources for Founders</h2>
          <p className="mt-2 text-gray-100">Practical guides to help you plan, build, and scale better.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "SaaS Architecture Guide",
              desc: "15-page guide covering scalable architecture patterns, database design, and cost optimization.",
              icon: "📖",
              cta: "Download PDF",
            },
            {
              title: "Startup Tech Stack Checklist",
              desc: "Decision framework for choosing the right technologies to launch fast and hire efficiently.",
              icon: "✅",
              cta: "Get Checklist",
            },
            {
              title: "Product Launch Roadmap",
              desc: "Step-by-step roadmap from ideation to market launch with timeline estimates.",
              icon: "🗺️",
              cta: "View Template",
            },
          ].map((resource) => (
            <div
              key={resource.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 group"
            >
              <div className="text-5xl mb-3 transform group-hover:scale-125 transition-transform duration-300">{resource.icon}</div>
              <h3 className="font-bold text-white text-lg">{resource.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{resource.desc}</p>
              <a href="/contact" className="mt-4 inline-block text-amber-300 hover:text-amber-200 font-semibold text-sm transition-colors">
                {resource.cta} →
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">🤝 Let's work together</h2>
        <p className="mt-2 max-w-2xl text-gray-100">
          Have a project idea or need guidance? We're happy to help you plan the right solution.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="/contact" className="btn-gold">Schedule a call</a>
          <a className="link-gold" href="/contact">
            Contact us
          </a>
        </div>
      </section>
    </div>
  );
}
